import '../../../shared/style.css';

import React, { useMemo, useState } from 'react';
import { useToast } from '@economic/taco';
import { useLocation, useNavigate, Link } from 'react-router-dom';

import { useLocalStorage } from '@economic/react-app';
import { t, useLocalization, tBB } from '../../../../../../shared/components/l10n/l10n';
import { usePostQuery } from '../../../../../../shared/hooks/usePostQuery';
import internalApiClient from '../../../../../../shared/util/api/internalApiClient';
import {
    BulkInvoicesBookingInfoResult,
    BulkBookAndSendFormData,
    BulkBookAndSendData,
    BookBulkInvoicesRequest,
    BookBulkInvoicesResponse,
    InvoicingMessageType,
} from '../types';
import { BulkBookDraftInvoicesDialog } from './BulkBookDraftInvoicesDialog';
import { InvoiceLayoutType } from '../../../../../../../types/internal-api-extra';
import { useEmailTemplates } from '../../utils/useEmailTemplates';
import { emailTemplateFormState } from '../../utils/emailTemplateFormState';
import { emailOptionsFromTemplates } from '../../utils/emailOptionsFromTemplates';

function LegacyBulkBookDraftInvoicesDialogContainer({ onBooked }: { onBooked: () => void }) {
    const location = useLocation();
    const navigate = useNavigate();
    const { draftIds } = location.state || ({} as any);

    function removeHashAfterHiding() {
        navigate(location.pathname);
    }

    return (
        <BulkBookDraftInvoicesDialogContainerContent onBooked={onBooked} draftIds={draftIds} onClose={removeHashAfterHiding} />
    );
}

function BulkBookDraftInvoicesDialogContainerContent({
    emailTemplateType,
    draftIds,
    onBooked,
    onClose,
}: {
    emailTemplateType?: InvoiceLayoutType;
    draftIds: number[];
    onBooked: () => void;
    onClose: () => void;
}) {
    const toast = useToast();
    const {
        loading,
        data,
        error: getInformationError,
        repost: refetchData,
    } = usePostQuery<BulkInvoicesBookingInfoResult>(
        'sales/invoice/GetBulkInformation',
        {
            invoiceDraftIds: draftIds,
        },
        !draftIds || draftIds.length === 0
    );

    const handleReload = () => {
        refetchData();
    };

    const [skipBookOnlyConfirmation, setSkipBookOnlyConfirmation] = useLocalStorage<boolean>('skipBulkBookOnlyConfirmation');
    const [includePaymentLink, setIncludePaymentLink] = useLocalStorage<boolean>('includePaymentLink');
    const [bookingInProgress, setBookingInProgress] = useState<boolean>(false);
    const [bookingError, setBookingError] = useState<any>();
    const { data: templates, loading: templatesLoading } = useEmailTemplates(emailTemplateType || InvoiceLayoutType.Invoice);

    const textsLoading = useLocalization(textIds);

    const transformedBulkBookingInfoResult = useMemo<BulkBookAndSendData | null>(() => {
        if (!data || textsLoading || templatesLoading) {
            return null;
        }

        const invoiceValidationErrorsById = new Map();
        data.draftInvoiceErrors.forEach(invoiceErrors =>
            invoiceValidationErrorsById.set(invoiceErrors.invoiceDraftId, invoiceErrors.messages)
        );
        const blockingErrors = data.draftInvoiceErrors
            .filter(e => e.messages.length > 0)
            .map(e => {
                return {
                    type: InvoicingMessageType.Error,
                    message: t('FORM_BOOKANDSEND_ERRORS_DRAFTNO', {
                        NUMBER: e.displayNumber,
                        ERROR: e.messages[0],
                    }),
                };
            });

        const invoicesToSendElectronically = data.sendElectronicInvoicesInfo.filter(i => i.canSend).map(i => i.draftId);
        const invoicesToSendByEmail = data.sendElectronicInvoicesInfo.filter(i => !i.canSend).map(i => i.draftId);

        // Sort messages in this order errors -> warnings -> info
        let nonBlockingWarnings = [...data.messages];
        nonBlockingWarnings.sort((a, b) => {
            if (a.type < b.type) return 1;
            if (a.type > b.type) return -1;
            return 0;
        });
        if (invoiceValidationErrorsById.size > 0) {
            nonBlockingWarnings = [
                {
                    type: InvoicingMessageType.Warning,
                    message: t('FORM_BULKBOOKANDSEND_WARNING_INVOICESSKIPPED', { COUNT: invoiceValidationErrorsById.size }),
                },
                ...nonBlockingWarnings,
            ];
        }

        return {
            viewData: {
                sendElectronicallyCount: invoicesToSendElectronically.length,
                sendByEmailCount: invoicesToSendByEmail.length,
                skippedCount: invoiceValidationErrorsById.size,
                nonBlockingWarnings,
                blockingErrors,
                skipBookOnlyConfirmation,
                canIncludePaymentLink: data.canIncludePaymentLink,
                retrySproomCheck: data.retrySproomCheck,
                exceededSendingLimit: data.exceededSendingLimit,
            },
            formData: {
                bookOnly: false,
                changeInvoiceDate: true,
                newInvoiceDate: data.newInvoiceDate,
                showInvoicesAfterBooking: data.checkedShowPdf,
                invoicesToSendElectronically,
                invoicesToSendByEmail,
                skipBookOnlyConfirmation,
                includePaymentLink,
                emailOptions: {
                    subject: data.subject,
                    body: data.body,
                    includeOioAsAttachment: data.checkedAttachOIOXML,
                    includePdfAsAttachment: data.checkedAttachPdf,
                    encrypt: data.checkedEncrypt,
                    includeOioLink: data.checkedEmailLinkOIOXML,
                    includePdfLink: data.checkedEmailLinkPdf,
                    ...emailOptionsFromTemplates(templates),
                },
                ...emailTemplateFormState(templates),
                retrySproomCheck: data.retrySproomCheck,
            },
        };
    }, [data, skipBookOnlyConfirmation, includePaymentLink, textsLoading, templates]);

    async function bulkBookAndSend(request: BookBulkInvoicesRequest) {
        return await internalApiClient.post<BookBulkInvoicesResponse>('/sales/invoice/BulkBookAndSend', request);
    }

    async function submit(formData: BulkBookAndSendFormData) {
        const request: BookBulkInvoicesRequest = {
            bookOnly: formData.bookOnly,
            newInvoiceDate: formData.changeInvoiceDate ? formData.newInvoiceDate : null,
            showInvoicesAfterBooking: formData.showInvoicesAfterBooking,
            includePaymentLink: formData.includePaymentLink,
            emailOptions: formData.emailOptions,
            invoicesToSendByEmail: formData.invoicesToSendByEmail,
            invoicesToSendElectronically: formData.invoicesToSendElectronically,
            retrySproomCheck: formData.retrySproomCheck,
        };

        setBookingInProgress(true);

        let response;
        try {
            response = await bulkBookAndSend(request);
        } catch (error) {
            setBookingError(error);
        }

        if (response.data?.bookedInvoices && response.data.bookedInvoices.length > 0) {
            const goToLink = formData.bookOnly ? '/sales/invoicing/archive' : '/sales/invoicing/sent';
            const toastTextId = formData.bookOnly
                ? 'TOAST_INVOICE_BOOK_PLURAL_SUCCESS'
                : 'TOAST_INVOICE_BOOK_AND_SEND_PLURAL_SUCCESS';
            const toastContent = tBB(toastTextId, {
                COUNT: response.data.bookedInvoices.length,
                __LINK: content => <Link to={goToLink}>{content}</Link>,
            });

            toast.success(toastContent);
        }

        if (response.data && response.data.failedToBookCount > 0) {
            toast.error(t('TOAST_INVOICE_BOOK_AND_SEND_PLURAL_FAILED_TO_BOOK_ERROR', { COUNT: response.data.failedToBookCount }));
        }

        if (response.data && response.data.failedToSendCount > 0) {
            toast.information(t('TOAST_INVOICE_BOOK_AND_SEND_PLURAL_FAILED_TO_SEND', { COUNT: response.data.failedToSendCount }));
        }

        setSkipBookOnlyConfirmation(formData.skipBookOnlyConfirmation);
        setIncludePaymentLink(formData.includePaymentLink);

        if (formData.showInvoicesAfterBooking && response.data?.pdfLink) {
            window.top?.firebirdTask.then(() => window.top?.PopUp2(response.data.pdfLink, 500, 600));
        }

        setBookingInProgress(false);
        onBooked();
    }

    return (
        <BulkBookDraftInvoicesDialog
            loading={loading || bookingInProgress || templatesLoading}
            onHide={onClose}
            onSubmit={submit}
            onReload={handleReload}
            viewData={transformedBulkBookingInfoResult?.viewData}
            formData={transformedBulkBookingInfoResult?.formData}
            error={getInformationError || bookingError}
            defaultOpen
        />
    );
}

const textIds = [
    'LOADING_TEXT1',
    'LOADING_TEXT2',
    'LOADING_TEXT3',
    'FORM_BOOKANDSEND_ERRORS_HEADER_CANNOTBOOK',
    'TOAST_INVOICE_BOOK_PLURAL_SUCCESS',
    'TOAST_INVOICE_BOOK_AND_SEND_PLURAL_SUCCESS',
    'TOAST_INVOICE_BOOK_AND_SEND_PLURAL_FAILED_TO_BOOK_ERROR',
    'TOAST_INVOICE_BOOK_AND_SEND_PLURAL_FAILED_TO_SEND',
    'FORM_BULKBOOKANDSEND_NOTIFICATION_INFORMATION_BODY',
    'FORM_BOOKANDSEND_ERRORS_UNKNOWN_ERROR',
    'FORM_BOOKANDSEND_ERRORS_DRAFTNO',
    'FORM_BULKBOOKANDSEND_TITLE',
    'FORM_BULKBOOKANDSEND_ELECTRONIC_HEADER',
    'FORM_BULKBOOKANDSEND_ELECTRONIC_DESCRIPTION',
    'FORM_BULKBOOKANDSEND_EMAIL_HEADER',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_CLOSE',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_OPEN',
    'FORM_BULKBOOKANDSEND_OPTIONS_CHANGEINVOICEDATETO',
    'FORM_BOOKANDSEND_EMAIL_INCLUDE_PAYMENT_LINK',
    'FORM_BOOKANDSEND_SHOW_INVOICE',
    'FORM_BOOKANDSEND_BOOK_TEXT',
    'FORM_BOOKANDSEND_SEND_TEXT',
    'FORM_BOOKANDSEND_BOOK_ONLY_CONFIRMATION_TEXT',
    'FORM_BOOKANDSEND_BOOK_ONLY_CONFIRMATION_DO_NOT_SHOW',
    'FORM_BOOKANDSEND_BACK',
    'FORM_BULKBOOKANDSEND_WARNING_INVOICESSKIPPED',
    'FORM_BOOKANDSEND_ERRORS_DRAFTNO',
];

export { LegacyBulkBookDraftInvoicesDialogContainer };
export default BulkBookDraftInvoicesDialogContainerContent;
